import { Stack, SwipeableDrawer } from '@ethings/ui-toolkit';
import { Box, styled, Theme, useTheme } from '@mui/material';
import { FC, PointerEvent, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { testid } from '../../../util';
import { FlexBox } from '../../flex-box';

interface LayoutMobileProps extends PropsWithChildren {
  handleCloseButtonClick: () => void;
  initialSnapPoint?: 'peek' | 'full';
  isPopupOpen: boolean;
}

type SnapPoint = 'peek' | 'full';

const PEEK_HEIGHT_PX = 250;
const FULL_HEIGHT = 'calc(100vh - 55px)'; // 55px is the height of the puller
const SNAP_DRAG_THRESHOLD_PX = 24;

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  position: 'relative',
  zIndex: 'unset',
  '& .MuiDrawer-paper': {
    background: theme.mapPopup.background,
    color: theme.mapPopup.contrastText,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    boxShadow: theme.shadows[1],
  },
}));

const Puller = styled(Box)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.drawer.puller,
  borderRadius: 3,
  display: 'block',
  height: 6,
  left: 'calc(50% - 15px)',
  position: 'absolute',
  top: 8,
  width: 30,
}));

export const LayoutMobile: FC<LayoutMobileProps> = ({ children, handleCloseButtonClick, initialSnapPoint = 'peek', isPopupOpen }) => {
  const { thingId } = useParams();
  const theme = useTheme();
  const [snapPoint, setSnapPoint] = useState<SnapPoint>(initialSnapPoint);
  const dragStartYRef = useRef<number | null>(null);
  const dragLastYRef = useRef<number | null>(null);
  const dragPointerIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPopupOpen) {
      setSnapPoint(initialSnapPoint);
    }
  }, [isPopupOpen, initialSnapPoint]);

  const handlePullerPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    dragStartYRef.current = event.clientY;
    dragLastYRef.current = event.clientY;
    dragPointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePullerPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragPointerIdRef.current !== event.pointerId) return;
    dragLastYRef.current = event.clientY;
  };

  const handlePullerDragEnd = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragPointerIdRef.current !== event.pointerId) return;

    const dragStartY = dragStartYRef.current;
    const dragEndY = dragLastYRef.current ?? event.clientY;

    dragStartYRef.current = null;
    dragLastYRef.current = null;
    dragPointerIdRef.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (dragStartY === null) return;

    const dragDistance = dragStartY - dragEndY;
    if (dragDistance > SNAP_DRAG_THRESHOLD_PX) {
      setSnapPoint('full');
      return;
    }

    if (dragDistance < -SNAP_DRAG_THRESHOLD_PX) {
      if (snapPoint === 'full') {
        setSnapPoint('peek');
        return;
      }

      handleCloseButtonClick();
    }
  };

  const drawerHeight = snapPoint === 'peek' ? `${PEEK_HEIGHT_PX}px` : FULL_HEIGHT;

  return (
    <StyledSwipeableDrawer
      anchor="bottom"
      disableDiscovery={true}
      disableEnforceFocus={true}
      disableSwipeToOpen={true}
      onClose={handleCloseButtonClick}
      onOpen={() => { }}
      onClick={(e) => { if (snapPoint === 'peek') e.stopPropagation(); setSnapPoint('full'); }}
      open={isPopupOpen}
      PaperProps={{
        sx: {
          maxHeight: '100vh',
          overflow: 'hidden',
        },
      }}
    >
      <FlexBox
        flexDirection="column"
        sx={{
          height: drawerHeight,
          overflowX: 'hidden',
          overflowY: 'hidden',
          transition: theme.transitions.create('height', {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeOut,
          }),
        }}
      >
        <Box
          onPointerDown={handlePullerPointerDown}
          onPointerMove={handlePullerPointerMove}
          onPointerUp={handlePullerDragEnd}
          onPointerCancel={handlePullerDragEnd}
          sx={{
            cursor: 'ns-resize',
            height: 24,
            position: 'relative',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          <Puller theme={theme} />
        </Box>
        <Stack data-testid={testid`thing-map-popup-${thingId}`} p={theme.mapPopup.padding} flex={1} minHeight={0} width='auto'>
          {children}
        </Stack>
        {snapPoint === 'peek' && (
          <Box
            sx={{
              background: `linear-gradient(180deg, transparent 0%, ${theme.mapPopup.background} 100%)`,
              bottom: 0,
              height: '100px',
              left: 0,
              pointerEvents: 'none',
              position: 'fixed',
              width: '100%',
            }}
          />
        )}
      </FlexBox>
    </StyledSwipeableDrawer>
  );
};
